// ─── Shader pixel scale ────────────────────────────────────────────────────────
// x4 on desktop (>= 1030px), x2 on mobile (<1030px), x1 on tiny (<490px).
// currentDivider is read each frame inside render() via resizeCanvasToDisplaySize.
const SHADER_BREAKPT_MOBILE = 1030;
const SHADER_BREAKPT_X1     = 490;

function getShaderDivider() {
    return window.innerWidth < SHADER_BREAKPT_X1     ? 1
         : window.innerWidth < SHADER_BREAKPT_MOBILE ? 2
         : 4;
}

let currentDivider = getShaderDivider();

// Update tier on resize so the next render frame picks up the right divider
window.addEventListener('resize', () => {
    const newDivider = getShaderDivider();
    if (newDivider !== currentDivider) {
        currentDivider = newDivider;
    }
});

async function loadShaderSource(filePath) {
    try {
        const response = await fetch(filePath);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}, path: ${filePath}`);
        }
        return await response.text();
    } catch (error) {
        console.error(`Failed to load shader file: ${filePath}`);
        throw error; // Rethrow to handle in the calling code
    }
}

async function initShaderCanvas() {
    const canvas = document.getElementById("shaderCanvas");
    const gl = canvas.getContext("webgl2");

    if (!gl) {
        console.error("WebGL2 not supported in this browser.");
        return;
    }

    // Resize canvas
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const vertexShaderSource = simple_vertex;
    const fragmentShaderSource = fog_dither_frag;

    // Compile and link shaders
    function createShader(gl, type, source) {
        const shader = gl.createShader(type);
        gl.shaderSource(shader, source);
        gl.compileShader(shader);
        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
            console.error(gl.getShaderInfoLog(shader));
            gl.deleteShader(shader);
            return null;
        }
        return shader;
    }

    function createProgram(gl, vertexShader, fragmentShader) {
        const program = gl.createProgram();
        gl.attachShader(program, vertexShader);
        gl.attachShader(program, fragmentShader);
        gl.linkProgram(program);
        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
            console.error(gl.getProgramInfoLog(program));
            gl.deleteProgram(program);
            return null;
        }
        return program;
    }

    const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource);
    if (!vertexShader || !fragmentShader) return;

    const program = createProgram(gl, vertexShader, fragmentShader);
    if (!program) return;

    // Set up a fullscreen quad
    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(
        gl.ARRAY_BUFFER,
        new Float32Array([
            -1, -1, 1, -1, -1, 1,
            -1, 1, 1, -1, 1, 1,
        ]),
        gl.STATIC_DRAW
    );

    const positionLocation = gl.getAttribLocation(program, "a_position");

    // Use the program
    gl.useProgram(program);

    // Get uniform locations
    const iResolutionLocation = gl.getUniformLocation(program, "custom_e914e45bb5c56267");
    const iTimeLocation = gl.getUniformLocation(program, "custom_905c3f97a4912a69");
    const iDateLocation = gl.getUniformLocation(program, "custom_eece0e40dafb39c6");

    const iChannel0Location = gl.getUniformLocation(program, "custom_ceb2d6eea8801718");
    const iChannelResolutionLocation = gl.getUniformLocation(program, "custom_c2308a19d7a17a82");

    async function setupTextures(gl, channel0path, iChannel0Location, iChannelResolutionLocation) {
        const iChannel0 = gl.createTexture();
    
        gl.bindTexture(gl.TEXTURE_2D, iChannel0);
    
        // Configure texture parameters for repeating
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    
        const image = new Image();
        image.src = channel0path;
    
        return new Promise((resolve, reject) => {
            image.onload = () => {
                // Upload the image to the texture
                gl.bindTexture(gl.TEXTURE_2D, iChannel0);
                gl.texImage2D(
                    gl.TEXTURE_2D,
                    0,
                    gl.RGBA,
                    gl.RGBA,
                    gl.UNSIGNED_BYTE,
                    image
                );
    
                // Check if the image dimensions are power of two
                if (isPowerOf2(image.width) && isPowerOf2(image.height)) {
                    // Generate mipmaps for better scaling
                    gl.generateMipmap(gl.TEXTURE_2D);
                } else {
                    // Use non-mipmap scaling
                    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
                    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
                }
    
                // Pass the texture unit to the shader
                gl.uniform1i(iChannel0Location, 0);
    
                // Set the resolution of the texture
                gl.uniform3f(iChannelResolutionLocation, image.width, image.height, 1.0);
    
                resolve(iChannel0);
            };
    
            image.onerror = (error) => {
                reject(new Error(`Failed to load texture at path: ${channel0path}`));
            };
        });
    }

    let iChannel0;
    try {
        iChannel0 = await setupTextures(
            gl,
            '/assets/images/4x4_bayer_matrix.png',
            iChannel0Location,
            iChannelResolutionLocation
        );
    } catch (error) {
        console.error("Error loading textures:", error);
        return;
    }

    function updateDateUniform(gl, program, iDateLocation) {
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth(); // JavaScript months are 0-based
        const day = now.getDate();
        const seconds = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
    
        // Pass the date as a vec4 (year, month, day, fractional day)
        gl.uniform4f(iDateLocation, year, month, day, seconds);
    }

    // Inside the render function
    function render(time) {
        time *= 0.001; // Convert to seconds

        resizeCanvasToDisplaySize(gl.canvas, currentDivider);

        // Update the canvas size if needed
        gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);
        gl.clear(gl.COLOR_BUFFER_BIT);

        // Update the iDate uniform
        updateDateUniform(gl, program, iDateLocation);

        // Bind textures
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, iChannel0);

        // Update other uniforms
        gl.uniform3f(iResolutionLocation, gl.canvas.width, gl.canvas.height, 1.0);
        gl.uniform1f(iTimeLocation, time);

        // Set other uniforms like iResolution and iTime
        gl.uniform3f(iResolutionLocation, gl.canvas.width, gl.canvas.height, 1.0);
        gl.uniform1f(iTimeLocation, time);

        // Bind position buffer and set attribute
        gl.enableVertexAttribArray(positionLocation);
        gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
        gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

        // Draw the quad
        gl.drawArrays(gl.TRIANGLES, 0, 6);

        // Loop
        requestAnimationFrame(render);
    }

    requestAnimationFrame(render);
}

function resizeCanvasToDisplaySize(canvas, divider) {
    const dividedClientWidth = Math.floor(canvas.clientWidth / divider);
    const dividedClientHeight = Math.floor(canvas.clientHeight / divider);

    // Check if the canvas size needs to be changed
    if (canvas.width !== dividedClientWidth || canvas.height !== dividedClientHeight) {
        canvas.width = dividedClientWidth;
        canvas.height = dividedClientHeight;
    }

    canvas.style.imageRendering = "pixelated";
}

function createTexture(gl, image) {
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);

    // Set texture parameters
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

    // Upload the image into the texture
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);

    return texture;
}

async function loadTexture(gl, url) {
    const image = new Image();
    return new Promise((resolve, reject) => {
        image.onload = () => resolve(createTexture(gl, image));
        image.onerror = reject;
        image.src = url;
    });
}

// Utility function to check for power-of-two dimensions
function isPowerOf2(value) {
    return (value & (value - 1)) === 0;
}